import { trackEvent } from "@/lib/analytics";

import type { DayStyle } from "./day-styles";
import type { AreaSport } from "./graph-types";
import { MOUNTAIN_AREA_SLUG_MAX_LENGTH } from "./mountain-community";
import { parseRouteOutcome } from "./route-outcomes";

export const PLANNER_ANALYTICS_EVENTS = [
  "planner_generation_started",
  "planner_generation_succeeded",
  "planner_generation_failed",
  "planner_route_saved",
  "planner_route_reopened",
  "planner_route_started",
  "planner_route_completed",
  "planner_route_feedback",
  "planner_style_selected",
  "planner_route_b_swapped",
  "planner_leg_swapped",
  "planner_stop_edited",
  "planner_replanned",
  "planner_plan_abandoned",
] as const;

export const MOUNTAIN_COMMUNITY_ANALYTICS_EVENTS = [
  "mountain_contribution_opened",
  "mountain_contribution_submitted",
  "mountain_comment_reported",
  "mountain_comment_moderated",
] as const;

export type MountainCommunityAnalyticsEvent =
  (typeof MOUNTAIN_COMMUNITY_ANALYTICS_EVENTS)[number];

export type MountainCommunityAnalyticsProperties = {
  areaSlug: string;
  sport: AreaSport;
  featureType: "trail" | "lift";
  featureId: string;
  tagCount?: number;
  commentPresent?: boolean;
  moderationAction?: "approve" | "reject" | "hide" | "retain";
};

const AREA_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MODERATION_ACTIONS = new Set(["approve", "reject", "hide", "retain"]);
const MOUNTAIN_COMMUNITY_EVENT_NAMES = new Set<string>(
  MOUNTAIN_COMMUNITY_ANALYTICS_EVENTS,
);

export type PlannerAnalyticsEvent = (typeof PLANNER_ANALYTICS_EVENTS)[number];

export type PlannerAnalyticsProperties = {
  slug: string;
  sport: AreaSport;
  dayStyle: DayStyle;
  source?: string;
  action?: string;
  /** Request to usable response, including transport and parsing. */
  durationMs?: number;
};

export function trackPlannerEvent(
  event: PlannerAnalyticsEvent,
  properties: PlannerAnalyticsProperties,
): void {
  trackEvent(event, {
    slug: properties.slug.slice(0, 100), sport: properties.sport, dayStyle: properties.dayStyle,
    ...(properties.source ? { source: properties.source.slice(0, 60) } : {}),
    ...(properties.action ? { action: properties.action.slice(0, 60) } : {}),
    ...((event === "planner_generation_succeeded" || event === "planner_generation_failed")
      && typeof properties.durationMs === "number" && Number.isFinite(properties.durationMs)
      && properties.durationMs >= 0 && properties.durationMs <= 120_000
      ? { duration_ms: Math.round(properties.durationMs / 10) * 10 } : {}),
  });
}

/** Only completed, explicit responses enter the existing consent-aware path.
 * Revisions share a journey id so analysis can retain the latest answer. */
export function trackRouteOutcome(raw: unknown): void {
  const outcome = parseRouteOutcome(raw);
  if (!outcome) return;
  const statusAge = outcome.evidence.oldestLiftStatusAt && outcome.evidence.generatedAt
    ? (Date.parse(outcome.evidence.generatedAt) - Date.parse(outcome.evidence.oldestLiftStatusAt)) / 60_000 : null;
  const roundedMinutes = (minutes: number | null) => minutes === null ? null : Math.round(minutes / 5) * 5;
  trackEvent("planner_route_feedback", {
    slug: outcome.slug, sport: outcome.sport, day_style: outcome.dayStyle,
    source: "completed-route", journey_id: outcome.journeyId, outcome_version: outcome.version,
    outcome_revision: outcome.revision, route_month: outcome.routeDate.slice(0, 7),
    assessment_version: outcome.assessmentVersion, journey_scope: outcome.journeyScope,
    selected_route: outcome.selectedRoute, would_ride_again: outcome.wouldRideAgain, reasons: outcome.reasons,
    planned_route_minutes: roundedMinutes(outcome.plannedRouteMinutes), requested_window_minutes: roundedMinutes(outcome.requestedWindowMinutes),
    actual_minutes: roundedMinutes(outcome.actualMinutes), timing_source: outcome.timingSource,
    timing_comparable: outcome.timingComparable, mean_trail_condition_score: outcome.meanTrailConditionScore,
    evidence_confidence: outcome.evidence.confidence, preview: outcome.evidence.preview,
    map_access_dated: outcome.evidence.mapAccessCheckedAt !== null,
    lift_status_age: statusAge === null || statusAge < -5 ? "unknown" : statusAge <= 60 ? "under-hour" : statusAge <= 1440 ? "same-day" : "older",
  });
}

/**
 * Builds the complete permitted analytics payload at runtime. Callers may pass
 * untrusted or over-wide objects, but private copy and unknown fields can never
 * reach PostHog through this path.
 */
export function sanitiseMountainCommunityAnalyticsProperties(
  event: MountainCommunityAnalyticsEvent,
  rawProperties: unknown,
): MountainCommunityAnalyticsProperties | null {
  if (!MOUNTAIN_COMMUNITY_EVENT_NAMES.has(event)) return null;
  if (!rawProperties || typeof rawProperties !== "object" || Array.isArray(rawProperties)) {
    return null;
  }
  const value = rawProperties as Record<string, unknown>;
  if (
    typeof value.areaSlug !== "string"
    || value.areaSlug.length > MOUNTAIN_AREA_SLUG_MAX_LENGTH
    || !AREA_SLUG.test(value.areaSlug)
    || (value.sport !== "ski" && value.sport !== "mtb")
    || (value.featureType !== "trail" && value.featureType !== "lift")
    || typeof value.featureId !== "string"
    || !UUID.test(value.featureId)
  ) {
    return null;
  }

  const safe: MountainCommunityAnalyticsProperties = {
    areaSlug: value.areaSlug,
    sport: value.sport,
    featureType: value.featureType,
    featureId: value.featureId,
  };
  if (event === "mountain_contribution_submitted") {
    if (
      typeof value.tagCount !== "number"
      || !Number.isInteger(value.tagCount)
      || value.tagCount < 0
      || value.tagCount > 5
      || typeof value.commentPresent !== "boolean"
    ) {
      return null;
    }
    safe.tagCount = value.tagCount;
    safe.commentPresent = value.commentPresent;
  }
  if (event === "mountain_comment_moderated") {
    if (typeof value.moderationAction !== "string" || !MODERATION_ACTIONS.has(value.moderationAction)) {
      return null;
    }
    safe.moderationAction = value.moderationAction as NonNullable<
      MountainCommunityAnalyticsProperties["moderationAction"]
    >;
  }
  return safe;
}

export function trackMountainCommunityEvent(
  event: MountainCommunityAnalyticsEvent,
  properties: unknown,
): void {
  const safeProperties = sanitiseMountainCommunityAnalyticsProperties(event, properties);
  if (!safeProperties) return;
  trackEvent(event, safeProperties);
}

export function plannerEventForEdit(
  kind: string,
): Extract<PlannerAnalyticsEvent, "planner_leg_swapped" | "planner_stop_edited"> | null {
  if (kind === "swap_run") return "planner_leg_swapped";
  if (
    kind === "add_stop" ||
    kind === "remove_stop" ||
    kind === "retime_stop" ||
    kind === "change_venue"
  ) {
    return "planner_stop_edited";
  }
  return null;
}
